import { useSettings } from '@/data/Settings';
import { COLOR_DRAWER_STATE_KEY } from '@/data/constants/hdmDataKey';
import { getStateUpdater, useInitState, useSetState, useTrackedState } from '@/data/state/provider';
import { ColorDrawerState } from '@/data/types/ColorType';
import { getStateKey } from '@/data/utils/getStateKey';
import { useCallback, useMemo } from 'react';

const COLOR_DRAWER_BASE: ColorDrawerState = {
	colorCode: null,
	colorCodeSelected: null,
	hasConfirmedColor: false,
	partNumber: null,
	initialized: false,
	isOpen: false,
	range: null,
	pendingRange: null,
	lineColorValue: null,
	base: null,
	rangeColors: null,
	superSKUAttributes: null,
	changeBaseColor: null,
};

/**
 * React hook for use by the presentation layer to read color drawer state
 * data and expose event handlers (actions) related to data changes.
 */
export const useColorDrawerState = () => {
	const { settings } = useSettings();
	const key = useMemo(() => getStateKey(COLOR_DRAWER_STATE_KEY, settings), [settings]);
	const baseState = useInitState(key, COLOR_DRAWER_BASE);
	const colorDrawerUpdater = useMemo(() => getStateUpdater({ key, baseState }), [baseState, key]);

	const setState = useSetState();
	const fullState = useTrackedState();
	const colorDrawerState = (fullState[key] as ColorDrawerState) || baseState;
	/**
	 * Action for general update
	 */
	const setColorDrawerData = useCallback(
		(data: Partial<Omit<ColorDrawerState, 'initialized'>>) =>
			colorDrawerUpdater({
				setState,
				now: (current) => ({ ...current, ...data }),
			}),
		[setState, colorDrawerUpdater]
	);

	/**
	 * Action for reset drawer data
	 */
	const resetColorDrawerData = useCallback(
		() =>
			colorDrawerUpdater({
				setState,
				now: () => ({ ...COLOR_DRAWER_BASE, initialized: true }),
			}),
		[setState, colorDrawerUpdater]
	);
	/**
	 * Action for open drawer
	 */
	const openBuyByColorDrawer = useCallback(
		(pendingRange: string) => {
			colorDrawerUpdater({
				setState,
				now: (current) => ({ ...current, isOpen: true, pendingRange }),
			});
		},
		[setState, colorDrawerUpdater]
	);
	const actions = useMemo(
		() => ({
			setColorDrawerData,
			resetColorDrawerData,
			openBuyByColorDrawer,
		}),
		[setColorDrawerData, resetColorDrawerData, openBuyByColorDrawer]
	);

	return {
		colorDrawerState,
		actions,
	};
};
